import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import Swal from "sweetalert2";
import Paginador from "./Paginador";
import GeneradorModal from "./GeneradorModal";
import { useFunctionsPrevia } from "../hooks/useFunctionsPrevia"; 

Modal.setAppElement("#root");

const BandejaReporte = ({
  reportes,
  onRowClick,
  selectedReporteId,
  guardarReporte,
  reporte, // Asegúrate de que este prop contenga el objeto completo del reporte seleccionado
  clientes,
}) => {
  const [filteredReportes, setFilteredReportes] = useState(reportes);
  const [showModal, setShowModal] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [showOptions, setShowOptions] = useState(false);
  const [showBulkActionsOptions, setShowBulkActionsOptions] = useState(false);
  const [selectedReportes, setSelectedReportes] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [itemsPerPage, setItemsPerPage] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const { formatDate } = useFunctionsPrevia();

  const totalPages = Math.ceil(filteredReportes.length / itemsPerPage);

  const paginatedReportes = filteredReportes.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const handlePageChange = (direction) => {
    if (direction === "next" && currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    } else if (direction === "prev" && currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleItemsPerPageChange = (event) => {
    setItemsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  };

  const handleSearch = (e) => {
    const searchTerm = e.target.value.toLowerCase();
    const results = [];

    reportes.forEach((reporte) => {
      let matchScore = 0;

      // 2) Nombre del cliente (+1)
      if ((reporte.nombreCliente || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }

      // 3) Número de remisión (con o sin "IC-") (+1)
      const numeroStr = (reporte.numeroReporte || "").toLowerCase();
      const numeroWithoutPrefix = numeroStr.replace(/^ic-/, "");
      if (
        numeroStr.includes(searchTerm) ||
        numeroWithoutPrefix.includes(searchTerm)
      ) {
        matchScore += 1;
      }

      // 4) Fecha de remisión (string original) (+1)
      if ((reporte.fechaReporte || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }

      // 5) Formatos alternativos de fecha (+1 si alguno coincide)
      if (reporte.fechaReporte) {
        const dateFormats = formatDate(new Date(reporte.fechaReporte), true);
        if (dateFormats.some((format) => format.includes(searchTerm))) {
          matchScore += 1;
        }
      }

      // 6) Importe formateado (con +2 para destacar)
      const formattedImporte = parseFloat(reporte?.total || 0)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      if (formattedImporte.includes(searchTerm)) {
        matchScore += 2;
      }

      // 7) Otros campos (area, asunto, atencion, cliente, createdAt) (+1)
      if ((reporte.area || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.ordenDeCompra || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.divisa || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.terminos || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.vendedorNombre || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.vendedorCorreo || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.referencia || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.notas || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.planta || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.asunto || "").toLowerCase().includes(searchTerm)) {
        matchScore += 1;
      }
      if ((reporte.usuario || "").toLowerCase().includes(searchTerm)) {
        matchScore += 2;
      }

      // Si el matchScore > 0, lo agregamos al array results
      if (matchScore > 0) {
        results.push({ ...reporte, matchScore });
      }
    });

    // Ordenamos los resultados de mayor a menor puntaje
    results.sort((a, b) => b.matchScore - a.matchScore);

    setFilteredReportes(results);
    setCurrentPage(1);
  };

  const openFormulario = () => {
    setIsFormDirty(false);
    setShowModal(true);
  };

  const closeModal = () => {
    if (isFormDirty) {
      Swal.fire({
        title: "¿Seguro que quieres salir?",
        text: "Se perderán los cambios realizados",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, salir",
        cancelButtonText: "Cancelar",
      }).then((result) => {
        if (result.isConfirmed) {
          setShowModal(false);
          setIsFormDirty(false); // Restablecer el estado formDirty
        }
      });
    } else {
      setShowModal(false);
    }
  };

  const toggleDropdown = () => {
    setShowDropdown(!showDropdown);
  };

  const toggleOptions = () => {
    setShowOptions(!showOptions);
  };

  const toggleBulkActionsOptions = () => {
    setShowBulkActionsOptions(!showBulkActionsOptions);
  };

  const handleSelectReporte = (remisionId) => {
    setSelectedReportes((prevSelected) =>
      prevSelected.includes(remisionId)
        ? prevSelected.filter((id) => id !== remisionId)
        : [...prevSelected, remisionId]
    );
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedReportes([]); // Deselecciona todo
    } else {
      setSelectedReportes(reportes.map((rem) => rem.id)); // Selecciona todo
    }
    setSelectAll(!selectAll);
  };

  // Corregido: usar selectedReportes.length en lugar de setSelectedReportes.length
  const showBulkActions = selectedReportes.length > 0;

  const exportarPDF = () => {
    console.log("Exportar como PDF");
  };

  const imprimir = () => {
    console.log("Imprimir reporte");
  };

  const eliminar = () => {
    console.log("Eliminar reporte");
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(".bulk-actions-right")) {
        setShowBulkActionsOptions(false);
      }
    };
    if (showBulkActionsOptions) {
      document.addEventListener("click", handleClickOutside);
    } else {
      document.removeEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [showBulkActionsOptions]);

  return (
    <div className="cotizaciones-bandeja">
      <div className="bandeja-search-form">
        <div className="bandeja-search-input-wrapper">
          <i className="bi bi-search"></i>
          <input
            type="text"
            className="bandeja-search-input"
            placeholder="Buscar..."
            onChange={handleSearch}
          />
        </div>
      </div>

      {showBulkActions && (
        <div className="bulk-actions">
          <div className="bulk-actions-left">
            <input
              type="checkbox"
              checked={selectAll}
              onChange={handleSelectAll}
              style={{ marginTop: "0", alignSelf: "center" }} // Ajuste para centrar el checkbox
            />
            <p>{selectedReportes.length} seleccionados</p>
          </div>

          <div className="bulk-actions-right-container">
            <button className="boton-accion">Marcar como enviado</button>
            <div
              className="bulk-actions-right"
              style={{ position: "relative" }}
            >
              <i
                className="bi bi-three-dots-vertical"
                onClick={toggleBulkActionsOptions}
                style={{ cursor: "pointer" }}
              ></i>
              {showBulkActionsOptions && (
                <div className="dropdown-options-bandeja">
                  <p onClick={exportarPDF}>
                    <i
                      style={{ fontSize: "14px" }}
                      className="bi bi-box-arrow-up-right"
                    ></i>
                    Exportar como PDF
                  </p>
                  <p onClick={imprimir}>
                    <i
                      style={{ fontSize: "14px" }}
                      className="bi bi-printer"
                    ></i>
                    Imprimir
                  </p>
                  <p onClick={eliminar}>
                    <i style={{ fontSize: "14px" }} className="bi bi-trash"></i>
                    Eliminar
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="encabezado">
        <div className="categoria-filtro" onClick={toggleDropdown}>
          <div style={{ display: "flex", alignItems: "center" }}>
            <p style={{ marginLeft: "10px" }}>Todos los reportes</p>
            <i
              className="bi bi-chevron-down"
              style={{ color: "#007bff", fontWeight: "bold" }}
            ></i>
          </div>
          {showDropdown && (
            <div className="dropdown-menu">
              <p>
                <i className="bi bi-envelope-check"></i>Reportes enviadas
              </p>
              <p>
                <i className="bi bi-clock"></i>Reportes pendientes
              </p>
            </div>
          )}
        </div>
        <div className="acciones-encabezado" style={{ position: "relative" }}>
          <button className="boton-accion" onClick={openFormulario}>
            + Nuevo
          </button>
          <div style={{ position: "relative", display: "inline-block" }}>
            <i
              className="bi bi-three-dots-vertical"
              onClick={toggleOptions}
              style={{ cursor: "pointer" }}
            ></i>
            {showOptions && (
              <div className="dropdown-options-bandeja">
                <p onClick={() => console.log("Ordenar")}>
                  <i className="bi bi-arrow-down-up"></i>Ordenar
                </p>
                <p onClick={() => console.log("Importar")}>
                  <i className="bi bi-box-arrow-in-down"></i>Importar
                </p>
                <p onClick={() => console.log("Exportar")}>
                  <i className="bi bi-box-arrow-up"></i>Exportar
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {paginatedReportes.length > 0 ? (
        paginatedReportes.map((reporte) => (
          <div
            key={reporte.id}
            className={`mensaje-cotizacion ${
              selectedReporteId === reporte.id ? "mensaje-seleccionado" : ""
            }`}
            onClick={() => onRowClick(reporte.id)}
            tabIndex="0"
          >
            <div className="info-container">
              <input
                type="checkbox"
                onChange={() => handleSelectReporte(reporte.id)}
                checked={selectedReportes.includes(reporte.id)}
              />
              <div className="info-column">
                <div className="cliente-importe">
                  <span className="cliente-nombre">
                    {reporte.nombreCliente}
                  </span>
                  <span className="importe">
                    {reporte.divisa}{" "}
                    {parseFloat(reporte?.total)
                      ?.toFixed(2)
                      .replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
                  </span>
                </div>
                <div className="fecha-referencia">
                  <p>{String(reporte.numeroReporte || "").padStart(4, "0")}</p>
                  <span className="punto">•</span>
                  <p>{formatDate(new Date(reporte.fechaReporte))}</p>
                </div>
                <div style={{ marginTop: "5px" }}>
                  <div
                    className={`status ${
                      reporte.estado === "Pendiente"
                        ? "pendiente"
                        : reporte.estado === "En Proceso"
                        ? "en-proceso"
                        : reporte.estado === "Facturado"
                        ? "facturado"
                        : "pagada" // Clase por defecto para el estado "Pagada"
                    }`}
                  >
                    {reporte.estado}
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))
      ) : (
        <div style={{ position: "relative", left: "5%", marginTop: "80px" }}>
          <p style={{ fontSize: "14px" }}>
            No existe ningún reporte que coincida con esta búsqueda.
          </p>

          <div className="sin-resultados-animation" style={{ left: "10%" }}>
            <img
              src="/img/noSearch.svg"
              alt="Sin resultados"
              className="sin-resultados-imagen"
            />
          </div>
        </div>
      )}

      <div className="paginador">
        <div style={{ paddingLeft: "10px", width: "34%" }}>
          <Paginador
            totalItems={filteredReportes.length}
            itemsPerPage={itemsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
          />
        </div>
      </div>

      <Modal
        isOpen={showModal}
        onRequestClose={closeModal}
        contentLabel="Nuevo Reporte"
        style={{
          overlay: {
            zIndex: 2,
          },
          content: {
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            paddingLeft: 0,
            padding: 0,
            fontFamily: "Roboto, sans-serif",
            "::WebkitScrollbar": {
              width: "0px",
              background: "transparent",
            },
            msOverflowStyle: "none",
            scrollbarWidth: "none",
          },
        }}
      >
        <button onClick={closeModal} className="cerrar-button">
          <i className="bi bi-x-lg"></i>
        </button>

        <GeneradorModal
          clientes={clientes}
          guardarReporte={guardarReporte}
          // IMPORTANTE: Asegúrate de que aquí se pase el objeto completo del reporte seleccionado,
          // para que al editar se muestren los datos correctos.
          reporte={reporte}
          onFormDirtyChange={(dirty) => setIsFormDirty(dirty)}
        />
      </Modal>
    </div>
  );
};

export default BandejaReporte;
